import { alpha, Avatar, Box, Grid, Typography } from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { formatUSPhone } from "../../../../../utils";
import { quoteModern } from "../style/style";

const clean = (value?: any) => String(value ?? "").trim();

const formatText = (value?: any) => {
  const normalized = clean(value);

  if (!normalized) return "";

  const textMap: Record<string, string> = {
    residencial: "Residential",
    residential: "Residential",
    commercial: "Commercial",
    business: "Business",
  };

  const lowerValue = normalized.toLowerCase();

  if (textMap[lowerValue]) return textMap[lowerValue];

  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const getInitials = (name: string) => {
  const parts = clean(name).split(" ").filter(Boolean);

  if (parts.length === 0) return "?";

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const getClientName = (client: any) => {
  const fullName = [client?.firstName, client?.lastName].map(clean).filter(Boolean).join(" ");

  return fullName || clean(client?.name) || clean(client?.fullName) || clean(client?.alias) || "Unnamed Client";
};

const getClientEmail = (client: any) => {
  if (Array.isArray(client?.emails) && client.emails.length > 0) {
    const defaultEmail = client.emails.find((item) => item?.isDefault)?.email || client.emails[0]?.email;

    if (clean(defaultEmail)) return clean(defaultEmail);
  }

  return clean(client?.email) || "No email";
};

const getClientPhone = (client: any) => {
  if (Array.isArray(client?.phoneNumbers) && client.phoneNumbers.length > 0) {
    const defaultPhone = client.phoneNumbers.find((item) => item?.isDefault) || client.phoneNumbers[0];

    const fullPhone = clean(defaultPhone?.phoneNumber);

    if (fullPhone) return fullPhone;

    const dialCode = clean(defaultPhone?.dialCode);
    const rawPhone = clean(defaultPhone?.phone) || clean(defaultPhone?.number);

    if (rawPhone) return `${dialCode}${rawPhone}`.trim();
  }

  const phone = clean(client?.phone) || clean(client?.phoneNumber);
  const dialCode = clean(client?.dialCode);

  if (phone) return `${dialCode}${phone}`.trim();

  return "No phone";
};

const getClientLocation = (coverageArea?: any) => {
  if (!coverageArea) return "No location";

  if (typeof coverageArea.location === "string") {
    return clean(coverageArea.location) || "No location";
  }

  const source = coverageArea.location || coverageArea;

  return (
    [source.address, source.city, source.state, source.zipCode || source.postalCode, source.country].map(clean).filter(Boolean).join(", ") ||
    "No location"
  );
};

const getClientType = (client: any) => {
  return formatText(client?.type || client?.clientType || client?.contactType) || "N/A";
};

const getClientCompanyName = (client: any) => {
  return clean(client?.clientCompanyName) || clean(client?.companyName) || clean(client?.company?.name);
};

const formatPhoneDisplay = (phone: string) => {
  if (!phone || phone === "No phone") return "No phone";

  try {
    return formatUSPhone(phone);
  } catch {
    return phone;
  }
};

const InfoCard = ({ icon, label, value }) => {
  return (
    <Box
      sx={{
        p: 1.25,
        height: "100%",
        borderRadius: 2,
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
        border: `1px solid ${quoteModern.colors.line}`,
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1.5,

          color: quoteModern.colors.orange,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={quoteModern.labelSx}>{label}</Typography>

        <Typography
          sx={{
            mt: 0.35,
            fontSize: 12.75,
            fontWeight: 800,
            color: quoteModern.colors.textPrimary,
            lineHeight: 1.35,
            wordBreak: "break-word",
          }}
        >
          {clean(value) || "-"}
        </Typography>
      </Box>
    </Box>
  );
};

const ClientChip = ({ label, value }) => {
  if (!clean(value)) return null;

  return (
    <Box
      sx={{
        px: 1,
        py: 0.55,
        borderRadius: 999,
        bgcolor: quoteModern.colors.orangeSoft,
        color: quoteModern.colors.orange,
        fontSize: 11.5,
        fontWeight: 900,
        lineHeight: 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Box
        component="span"
        sx={{
          color: alpha(quoteModern.colors.orange, 0.75),
          fontWeight: 800,
        }}
      >
        {label}:
      </Box>

      {value}
    </Box>
  );
};

type QuoteClientSectionProps = {
  client: any;
  coverageArea?: any;
};

export const QuoteClientSection = ({ client, coverageArea }: QuoteClientSectionProps) => {
  if (!client) return null;

  const fullName = getClientName(client);
  const alias = clean(client.alias);
  const email = getClientEmail(client);
  const phone = getClientPhone(client);
  const location = getClientLocation(coverageArea);
  const clientType = getClientType(client);
  const clientCompanyName = getClientCompanyName(client);
  const displayPhone = formatPhoneDisplay(phone);
  const shouldShowAlias = Boolean(alias && alias !== fullName);

  return (
    <Box sx={{ ...quoteModern.cardSx, mb: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Avatar
            src={clean(client?.avatar) || undefined}
            sx={{
              width: 46,
              height: 46,
              bgcolor: quoteModern.colors.orangeSoft,
              color: quoteModern.colors.orange,
              fontSize: 15,
              fontWeight: 900,
              border: `1px solid ${quoteModern.colors.line}`,
            }}
          >
            {getInitials(fullName)}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={quoteModern.labelSx}>Bill to</Typography>

            <Typography
              sx={{
                mt: 0.45,
                fontSize: 17,
                fontWeight: 900,
                color: quoteModern.colors.textPrimary,
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {fullName}
            </Typography>

            <Box
              sx={{
                mt: 0.75,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              <ClientChip label="Type" value={clientType} />

              {shouldShowAlias && <ClientChip label="Alias" value={alias} />}

              {clientCompanyName && <ClientChip label="Company" value={clientCompanyName} />}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            bgcolor: quoteModern.colors.orangeSoft,
            color: quoteModern.colors.orange,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      <Grid container spacing={1.25}>
        <Grid item xs={12} md={6}>
          <InfoCard icon={<EmailOutlinedIcon sx={{ fontSize: 17 }} />} label="Email" value={email} />
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard icon={<LocalPhoneOutlinedIcon sx={{ fontSize: 17 }} />} label="Phone" value={displayPhone} />
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard icon={<BadgeOutlinedIcon sx={{ fontSize: 17 }} />} label="Client type" value={clientType} />
        </Grid>

        {clientCompanyName && (
          <Grid item xs={12} md={6}>
            <InfoCard icon={<BusinessOutlinedIcon sx={{ fontSize: 17 }} />} label="Client company" value={clientCompanyName} />
          </Grid>
        )}

        {shouldShowAlias && (
          <Grid item xs={12} md={6}>
            <InfoCard icon={<PersonOutlineOutlinedIcon sx={{ fontSize: 17 }} />} label="Alias" value={alias} />
          </Grid>
        )}

        <Grid item xs={12}>
          <InfoCard icon={<HomeOutlinedIcon sx={{ fontSize: 17 }} />} label="Location" value={location} />
        </Grid>
      </Grid>
    </Box>
  );
};
